import { cn, Skeleton } from "@scribenote/brian-design-system";
import type { CardSeverity } from "@subjects/cardSeverity";
import { ViewReader } from "@views/ViewReader";
import { useViewDetail } from "@views/viewsQueries";
import type { ReactNode } from "react";

/**
 * A view fetched by uuid and rendered as a reading surface — the shared body of
 * every pane that holds only a uuid.
 *
 * Fetching looks redundant wherever the caller already holds the view in a list
 * payload, and is not: `GET /views/{uuid}` is what records the `detail` impression
 * BE-2151's attention report counts as a sighting. Render from a list copy and
 * every read reports as never shown, however often staff open it.
 *
 * Suspends on a cold cache, so hosts must supply a skeleton — {@link
 * ViewReaderSkeleton} — rather than a null fallback that blanks the pane.
 */
export const ViewByUuid = ({
  id,
  severity,
  onDeleted,
  trailing,
  lifted = false,
  withSubject = false,
}: {
  id: string;
  /** Subject-context severity for the reader chrome - see {@link ViewReader}. */
  severity?: CardSeverity;
  onDeleted: () => void;
  trailing?: ReactNode;
  /** The reader sheet's backdrop - see {@link ViewReader}. */
  lifted?: boolean;
  /** Name the patient in the masthead - see {@link ViewReader}. */
  withSubject?: boolean;
}) => {
  const { data: view } = useViewDetail(id);

  // A view edited after signing is superseded by a fresh uuid, so a held uuid can
  // be a dead one — follow it to the current head rather than rendering the stale
  // tree and booking the impression against a view nobody is reading.
  if (view.supersededBy)
    return (
      <ViewByUuid
        id={view.supersededBy}
        severity={severity}
        onDeleted={onDeleted}
        trailing={trailing}
        lifted={lifted}
        withSubject={withSubject}
      />
    );

  return (
    <ViewReader
      view={view}
      severity={severity}
      onDeleted={onDeleted}
      trailing={trailing}
      lifted={lifted}
      withSubject={withSubject}
    />
  );
};

/** The reader's loading state, shaped like the sheet it stands in for. */
export const ViewReaderSkeleton = ({
  lifted = false,
}: {
  lifted?: boolean;
}) => (
  <div className="flex h-full min-h-0 min-w-0 flex-col p-4">
    <div
      className={cn(
        "mx-auto flex min-h-0 w-full max-w-4xl flex-1 flex-col gap-4 rounded-paper border border-rule bg-card p-7",
        lifted ? "shadow-lift-lg" : "shadow-lift",
      )}
    >
      <Skeleton className="h-7 w-1/2" />
      <Skeleton className="h-40" />
    </div>
  </div>
);
